import { FC, ReactNode, ReactElement, MouseEvent as ReactMouseEvent, useMemo, useState, useRef, useEffect, Children, isValidElement } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import "highlight.js/styles/github.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faFilter, faFilterCircleXmark, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import ModalDefault from '../ModalDefault/ModalDefault';
import { isColumnNumeric, compareTexts } from './tableUtils';
import './MarkdownMessage.scss';
import useIsMobileApp from '../../hooks/useIsMobileApp';

interface MarkdownMessageProps {
  content: string;
  createdAt: string;
  isStreaming?: boolean;
}

interface MarkdownTableProps {
  className?: string;
  children?: ReactNode;
  [key: string]: any;
}

interface TableCell {
  node: ReactNode;
  text: string;
}

interface TableRow {
  key: number;
  cells: TableCell[];
}

interface SortConfig {
  columnIndex: number;
  direction: 'asc' | 'desc';
}

// Componentes nomeados para permitir identificar por referência (`child.type`)
// a estrutura thead/tbody/tr/th/td dentro dos children já processados pelo react-markdown.
const TheadPart: FC<{ className?: string; [key: string]: any }> = ({ className, ...props }) => (
  <thead className={className} {...props} />
);
const TbodyPart: FC<{ className?: string; [key: string]: any }> = ({ className, ...props }) => (
  <tbody className={className} {...props} />
);
const TrPart: FC<{ className?: string; [key: string]: any }> = ({ className, ...props }) => (
  <tr className={className} {...props} />
);

function getNodeText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getNodeText).join('');
  if (isValidElement(node)) {
    const props = node.props as { children?: ReactNode };
    return getNodeText(props?.children);
  }
  return '';
}

function toElementArray(node: ReactNode): ReactElement[] {
  return (Children.toArray(node) as ReactNode[]).filter(isValidElement) as ReactElement[];
}

function extractTableData(children: ReactNode): { headers: TableCell[]; rows: TableRow[] } | null {
  const topLevel = toElementArray(children);
  const theadEl = topLevel.find((c) => c.type === TheadPart);
  const tbodyEl = topLevel.find((c) => c.type === TbodyPart);

  if (!theadEl) return null;

  const headerRows = toElementArray((theadEl.props as any)?.children);
  const headerRowEl = headerRows.find((c) => c.type === TrPart);
  if (!headerRowEl) return null;

  const headerCellEls = toElementArray((headerRowEl.props as any)?.children);
  const headers: TableCell[] = headerCellEls.map((cellEl) => {
    const node = (cellEl.props as any)?.children;
    return { node, text: getNodeText(node).trim() };
  });

  const bodyRowEls = tbodyEl ? toElementArray((tbodyEl.props as any)?.children) : [];

  const rows: TableRow[] = bodyRowEls.map((rowEl, rowIndex) => {
    const cellEls = toElementArray((rowEl.props as any)?.children);
    return {
      key: rowIndex,
      cells: cellEls.map((cellEl) => {
        const node = (cellEl.props as any)?.children;
        return { node, text: getNodeText(node).trim() };
      }),
    };
  });

  return { headers, rows };
}

function compareCells(a: TableCell, b: TableCell, numeric: boolean, direction: 'asc' | 'desc'): number {
  return compareTexts(a.text, b.text, numeric, direction);
}

interface ColumnFilterPopupProps {
  anchorRect: DOMRect;
  container: HTMLElement;
  values: string[];
  selected: Set<string> | null;
  onApply: (selected: Set<string> | null) => void;
  onClose: () => void;
}

const ColumnFilterPopup: FC<ColumnFilterPopupProps> = ({ anchorRect, container, values, selected, onApply, onClose }) => {
  const [draft, setDraft] = useState<Set<string>>(() => new Set(selected ?? values));
  const [search, setSearch] = useState('');
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleScrollOrResize = (e: Event) => {
      // Scroll events bubble down via a fase de captura mesmo sem bubbling, então um
      // listener no window com capture=true também dispara para o scroll interno da
      // lista de valores (.markdown-table-filter-list). Isso fechava o popup assim que
      // o usuário tentava rolar a lista — ignoramos quando o alvo é o próprio popup.
      if (popupRef.current && e.target instanceof Node && popupRef.current.contains(e.target)) {
        return;
      }
      onClose();
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [onClose]);

  const filteredValues = useMemo(
    () => values.filter((v) => v.toLowerCase().includes(search.toLowerCase())),
    [values, search]
  );

  const toggleValue = (value: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  // `fixed` + coordenadas de getBoundingClientRect (já relativas ao viewport) evita ter que
  // somar/tratar scroll — funciona igual dentro do modal "Tela cheia" ou na página normal.
  const top = anchorRect.bottom + 4;
  const left = Math.min(anchorRect.left, window.innerWidth - 260);

  return createPortal(
    <div
      ref={popupRef}
      className="markdown-table-filter-popup"
      style={{ position: 'fixed', top, left }}
    >
      <input
        type="text"
        className="markdown-table-filter-search"
        placeholder="Buscar..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoFocus
      />

      <div className="markdown-table-filter-actions">
        <button type="button" onClick={() => setDraft(new Set(values))}>Selecionar todos</button>
        <button type="button" onClick={() => setDraft(new Set())}>Limpar</button>
      </div>

      <div className="markdown-table-filter-list">
        {filteredValues.map((value) => (
          <label key={value} className="markdown-table-filter-item">
            <input
              type="checkbox"
              checked={draft.has(value)}
              onChange={() => toggleValue(value)}
            />
            <span>{value || '(vazio)'}</span>
          </label>
        ))}
        {filteredValues.length === 0 && (
          <div className="markdown-table-filter-empty">Nenhum valor encontrado</div>
        )}
      </div>

      <div className="markdown-table-filter-footer">
        <button type="button" className="btn-cancel" onClick={onClose}>Cancelar</button>
        <button
          type="button"
          className="btn-apply"
          onClick={() => {
            onApply(draft.size === values.length ? null : draft);
            onClose();
          }}
        >
          Aplicar
        </button>
      </div>
    </div>,
    container
  );
};

const MarkdownTable: FC<MarkdownTableProps> = ({ className, children, node, ...props }) => {
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [filters, setFilters] = useState<Record<number, Set<string>>>({});
  const [activeFilterColumn, setActiveFilterColumn] = useState<number | null>(null);
  const [filterAnchorRect, setFilterAnchorRect] = useState<DOMRect | null>(null);
  const [filterPortalContainer, setFilterPortalContainer] = useState<HTMLElement | null>(null);

  const tableClassName = ["table table-striped", className].filter(Boolean).join(" ");

  const tableData = useMemo(() => extractTableData(children), [children]);

  const columnTypes = useMemo(() => {
    if (!tableData) return [];
    return tableData.headers.map((_, colIndex) =>
      isColumnNumeric(tableData.rows.map((r) => r.cells[colIndex]?.text ?? ''))
    );
  }, [tableData]);

  const uniqueValuesByColumn = useMemo(() => {
    if (!tableData) return [];
    return tableData.headers.map((_, colIndex) => {
      const seen = new Set<string>();
      tableData.rows.forEach((r) => seen.add(r.cells[colIndex]?.text ?? ''));
      return Array.from(seen).sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
    });
  }, [tableData]);

  const processedRows = useMemo(() => {
    if (!tableData) return [];
    let result = tableData.rows;

    const activeFilterEntries = Object.entries(filters);
    if (activeFilterEntries.length > 0) {
      result = result.filter((row) =>
        activeFilterEntries.every(([colIdxStr, allowed]) => {
          const colIdx = Number(colIdxStr);
          const text = row.cells[colIdx]?.text ?? '';
          return allowed.has(text);
        })
      );
    }

    if (sortConfig) {
      const { columnIndex, direction } = sortConfig;
      const numeric = columnTypes[columnIndex];
      result = [...result].sort((a, b) =>
        compareCells(a.cells[columnIndex], b.cells[columnIndex], numeric, direction)
      );
    }

    return result;
  }, [tableData, filters, sortConfig, columnTypes]);

  const handleHeaderClick = (colIndex: number) => {
    setSortConfig((prev) => {
      if (!prev || prev.columnIndex !== colIndex) return { columnIndex: colIndex, direction: 'asc' };
      if (prev.direction === 'asc') return { columnIndex: colIndex, direction: 'desc' };
      return null;
    });
  };

  const handleFilterIconClick = (colIndex: number, e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    // Quando o clique vem da tabela em "Tela cheia", o popup precisa ficar dentro do
    // próprio modal (react-bootstrap) — um portal para document.body vira "irmão" do
    // modal e fica sujeito ao aria-hidden/focus trap que ele aplica ao restante da página,
    // o que impedia o popup de abrir/funcionar dentro da tela cheia.
    const modalAncestor = e.currentTarget.closest('.modal') as HTMLElement | null;
    setFilterPortalContainer(modalAncestor ?? document.body);
    setFilterAnchorRect(e.currentTarget.getBoundingClientRect());
    setActiveFilterColumn(colIndex);
  };

  if (!tableData) {
    // Estrutura inesperada (fallback defensivo): preserva o comportamento original.
    return (
      <div className="markdown-table-block">
        <button
          type="button"
          className="markdown-table-fullscreen-btn"
          onClick={() => setShowFullscreen(true)}
          aria-label="Visualizar tabela em tela cheia"
        >
          <FontAwesomeIcon icon={faExpand} />
          <span>Tela cheia</span>
        </button>

        <div className="table-responsive">
          <table className={tableClassName} {...props}>{children}</table>
        </div>

        <ModalDefault
          title="Visualização da tabela"
          show={showFullscreen}
          onClose={() => setShowFullscreen(false)}
          sizeModal="xxl"
          showFooter={false}
          scrollable
          className="markdown-table-modal"
        >
          <div className="table-responsive">
            <table className={tableClassName} {...props}>{children}</table>
          </div>
        </ModalDefault>
      </div>
    );
  }

  const renderSortIcon = (colIndex: number) => {
    if (!sortConfig || sortConfig.columnIndex !== colIndex) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const renderTable = () => (
    <table className={tableClassName} {...props}>
      <thead>
        <tr>
          {tableData.headers.map((header, colIndex) => {
            const columnFilter = filters[colIndex];
            const isFiltered = columnFilter !== undefined;
            return (
              <th key={colIndex} className="align-middle markdown-table-th">
                <div className="markdown-table-th-content">
                  <button
                    type="button"
                    className="markdown-table-sort-btn"
                    onClick={() => handleHeaderClick(colIndex)}
                    aria-label={`Ordenar por ${header.text}`}
                  >
                    <span>{header.node}</span>
                    <FontAwesomeIcon icon={renderSortIcon(colIndex)} className="markdown-table-sort-icon" />
                  </button>
                  <button
                    type="button"
                    className={["markdown-table-filter-btn", isFiltered ? "active" : ""].filter(Boolean).join(" ")}
                    onClick={(e) => handleFilterIconClick(colIndex, e)}
                    aria-label={`Filtrar ${header.text}`}
                  >
                    <FontAwesomeIcon icon={isFiltered ? faFilterCircleXmark : faFilter} />
                  </button>
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {processedRows.map((row) => (
          <tr key={row.key}>
            {row.cells.map((cell, colIndex) => (
              <td key={colIndex} className="align-middle">{cell.node}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="markdown-table-block">
      <button
        type="button"
        className="markdown-table-fullscreen-btn"
        onClick={() => setShowFullscreen(true)}
        aria-label="Visualizar tabela em tela cheia"
      >
        <FontAwesomeIcon icon={faExpand} />
        <span>Tela cheia</span>
      </button>

      <div className="table-responsive">
        {renderTable()}
      </div>

      <ModalDefault
        title="Visualização da tabela"
        show={showFullscreen}
        onClose={() => setShowFullscreen(false)}
        sizeModal="xxl"
        showFooter={false}
        scrollable
        className="markdown-table-modal"
      >
        <div className="table-responsive">
          {renderTable()}
        </div>
      </ModalDefault>

      {activeFilterColumn !== null && filterAnchorRect && filterPortalContainer && (
        <ColumnFilterPopup
          anchorRect={filterAnchorRect}
          container={filterPortalContainer}
          values={uniqueValuesByColumn[activeFilterColumn]}
          selected={filters[activeFilterColumn] ?? null}
          onApply={(selected) => {
            setFilters((prev) => {
              const next = { ...prev };
              if (selected === null) {
                delete next[activeFilterColumn];
              } else {
                next[activeFilterColumn] = selected;
              }
              return next;
            });
          }}
          onClose={() => setActiveFilterColumn(null)}
        />
      )}
    </div>
  );
};

function unwrapMarkdownFencesStrict(src: string) {
  return src.replace(/```(?:markdown|md)\s*\n([\s\S]*?)\n```/g, (_m, inner) => inner.trim());
}

function unwrapMarkdownFencesLoose(src: string) {
  let out = src;

  // Remove apenas a PRIMEIRA abertura ```markdown/```md
  out = out.replace(/```(?:markdown|md)[^\n]*\n?/, "");

  // Se já chegou um fechamento solto no final, remove o PRIMEIRO fechamento remanescente
  out = out.replace(/```(?=[^`]*$)/, "");

  return out;
}

function promoteBracketHeading(src: string) {
  return src.replace(/^\[([^\]]+)\]\s*$/m, "### $1");
}

const MarkdownMessage: FC<MarkdownMessageProps> = ({ content, createdAt, isStreaming = false }) => {
  const isChatOnly = useIsMobileApp();
  const normalized = useMemo(() => {
    let s = content ?? "";

    s = isStreaming ? unwrapMarkdownFencesLoose(s) : unwrapMarkdownFencesStrict(s);

    s = promoteBracketHeading(s);

    s = s.replace(/\n{3,}/g, "\n\n");
    s = s.replace(/\\times/g, "x");

    return s;
  }, [content, isStreaming]);

  return (
    <div className="MarkdownMessage" data-testid="MarkdownMessage">
      <div>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeHighlight, rehypeRaw, rehypeSanitize, rehypeKatex]}
          components={{
            table: ({ node, className, children, ...props }) => (
              <MarkdownTable className={className} {...props}>
                {children}
              </MarkdownTable>
            ),
            thead: TheadPart,
            tbody: TbodyPart,
            tr: TrPart,
            th: ({ className, ...props }) => (
              <th className={["align-middle", className].filter(Boolean).join(" ")} {...props} />
            ),
            td: ({ className, ...props }) => (
              <td className={["align-middle", className].filter(Boolean).join(" ")} {...props} />
            ),
            // @ts-expect-error `inline` existe em runtime
            code({ inline, className, children, ...props }) {
              return inline ? (
                <code className="bg-gray-100 text-gray-800 px-1 rounded font-mono text-sm">
                  {children}
                </code>
              ) : (
                <pre className="text-gray-800 p-4 rounded-xl">
                  <code className={className} {...props}>
                    {children}
                  </code>
                </pre>
              );
            },
            a: ({ node, ...props }) => {
              // No mobile, links para artigos viram texto (não saem do chat).
              if (isChatOnly && /\/articles\//.test(props.href ?? "")) {
                return <span>{props.children}</span>;
              }
              return (
                <a
                  {...props}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              );
            },
          }}
        >
          {normalized}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default MarkdownMessage;
